if (!customElements.get('roll-label-splitting')) {
  const MIN_LABELS_PER_ROLL = 200;
  const MAX_LABELS_PER_ROLL = 2500;
  const ROLL_LABEL_INCREMENT = 50;
  const MAX_ROLL_SPLIT_COUNT = 25;

  customElements.define(
    'roll-label-splitting',
    class RollLabelSplitting extends HTMLElement {
      connectedCallback() {
        this.handleDocumentChange = this.handleDocumentChange.bind(this);
        this.handleDocumentInput = this.handleDocumentInput.bind(this);
        this.update = this.update.bind(this);

        document.addEventListener('change', this.handleDocumentChange);
        document.addEventListener('input', this.handleDocumentInput);
        document.addEventListener('variant:changed', this.update);

        this.update();
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.handleDocumentChange);
        document.removeEventListener('input', this.handleDocumentInput);
        document.removeEventListener('variant:changed', this.update);
      }

      parseQuantity(value) {
        const match = String(value || '').replace(/,/g, '').match(/\d+(?:\.\d+)?/);
        return match ? Number(match[0]) : 0;
      }

      formatRollCount(rollCount) {
        return `${rollCount.toLocaleString()} roll${rollCount === 1 ? '' : 's'}`;
      }

      formatLabelsPerRoll(labelsPerRoll) {
        return labelsPerRoll.toLocaleString();
      }

      formatRollLayout(rollCount, quantity) {
        return `${this.formatRollCount(rollCount)} of ${this.formatLabelsPerRoll(quantity / rollCount)}`;
      }

      formatRollProperty(rollCount, quantity) {
        return `${rollCount}_of_${quantity / rollCount}`;
      }

      getRollOptions(quantity) {
        const maxRollCount = Math.min(Math.floor(quantity / MIN_LABELS_PER_ROLL), MAX_ROLL_SPLIT_COUNT);
        const options = [];

        for (let rollCount = 2; rollCount <= maxRollCount; rollCount += 1) {
          const labelsPerRoll = quantity / rollCount;

          if (
            !Number.isInteger(labelsPerRoll) ||
            labelsPerRoll < MIN_LABELS_PER_ROLL ||
            labelsPerRoll > MAX_LABELS_PER_ROLL ||
            labelsPerRoll % ROLL_LABEL_INCREMENT !== 0
          ) {
            continue;
          }

          options.push({
            rollCount,
            label: this.formatRollLayout(rollCount, quantity),
          });
        }

        return options;
      }

      renderRollOptions(select, rollOptions, selectedRollCount) {
        if (!select) return;

        select.replaceChildren();

        const defaultOption = document.createElement('option');
        defaultOption.value = '0';
        defaultOption.textContent = rollOptions.length ? 'Automatic Roll Split' : 'Custom roll split unavailable';
        select.appendChild(defaultOption);

        rollOptions.forEach((rollOption) => {
          const option = document.createElement('option');
          option.value = String(rollOption.rollCount);
          option.textContent = rollOption.label;
          select.appendChild(option);
        });

        select.disabled = !rollOptions.length;
        select.value = String(selectedRollCount);
      }

      handleDocumentChange(event) {
        const productInfo = this.closest('product-info');
        if (productInfo?.contains(event.target)) this.update();
      }

      handleDocumentInput(event) {
        const productInfo = this.closest('product-info');
        if (event.target.matches('#ctm_quantity') && productInfo?.contains(event.target)) this.update();
      }

      update() {
        const productInfo = this.closest('product-info') || document;
        const customQuantity = productInfo.querySelector('.product_calculator_main.active #ctm_quantity');
        const selectedQuantity = productInfo.querySelector(
          '.sticker-quantity input:checked, .sticker-quantity select'
        );
        const quantity = this.parseQuantity(
          customQuantity?.value || selectedQuantity?.value || this.dataset.selectedQuantity
        );
        const splitSelect = this.querySelector('[data-roll-split-select]');
        const summary = this.querySelector('[data-roll-splitting-summary]');
        const additionalPrice = this.querySelector('[data-roll-splitting-additional-price]');
        const minimumMessage = this.querySelector('[data-roll-splitting-minimum]');
        const availableMessage = this.querySelector('[data-roll-splitting-available]');
        const maxSplitCountOutput = this.querySelector('[data-max-split-count]');
        const rollSplittingInput = this.querySelector('[data-roll-splitting-property-input]');
        const unitPrice = Number(this.dataset.rollSplittingUnitPrice || 0);
        const rollOptions = this.getRollOptions(quantity);
        const maxSplitCount = rollOptions[rollOptions.length - 1]?.rollCount || 0;
        const requestedRollCount = Math.max(0, Number(splitSelect?.value || 0));
        const selectedOption = rollOptions.find((rollOption) => rollOption.rollCount === requestedRollCount);
        const selectedRollCount = selectedOption?.rollCount || 0;
        const canSplit = rollOptions.length > 0;

        this.dataset.selectedRollCount = String(selectedRollCount);
        this.renderRollOptions(splitSelect, rollOptions, selectedRollCount);
        this.toggleAttribute('data-roll-splitting-disabled', !canSplit);

        const hasSelectedSplits = selectedRollCount > 0;
        const chargeableSplitCount = selectedRollCount;

        if (summary) summary.hidden = !hasSelectedSplits;
        if (additionalPrice) additionalPrice.hidden = !hasSelectedSplits;
        if (minimumMessage) minimumMessage.hidden = canSplit;
        if (availableMessage) availableMessage.hidden = !canSplit;
        if (maxSplitCountOutput) maxSplitCountOutput.textContent = maxSplitCount.toLocaleString();
        const rollSplitLayoutOutput = this.querySelector('[data-roll-split-layout]');
        const splitCountOutput = this.querySelector('[data-split-count]');
        const splitTotalOutput = this.querySelector('[data-split-total]');

        if (rollSplitLayoutOutput) rollSplitLayoutOutput.textContent = selectedOption?.label || '—';
        if (splitCountOutput) splitCountOutput.textContent = chargeableSplitCount.toLocaleString();
        if (splitTotalOutput) splitTotalOutput.textContent =
          `$${((unitPrice * chargeableSplitCount) / 100).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        if (rollSplittingInput) {
          rollSplittingInput.value = hasSelectedSplits
            ? this.formatRollProperty(selectedRollCount, quantity)
            : '';
          rollSplittingInput.disabled = !hasSelectedSplits;
        }
      }
    }
  );
}
